import { useState, useEffect } from "react"
import {
    TablePagination,
    TextField,
    Box,
    IconButton
} from "@mui/material"
import { ChevronLeft, ChevronRight } from "@mui/icons-material"
import { Body2 } from "../../themes/typography"

export default function Pagination({
    count = 0,
    page = 0,
    rowsPerPage = 10,
    onPageChange = () => { },
    onRowsPerPageChange = () => { },
    rowsPerPageOptions = [10, 25, 100],
}) {
    const totalPages = Math.ceil(count / rowsPerPage)

    function CustomActionControls() {
        const [inputValue, setInputValue] = useState(page + 1)

        // Sync input value when parent page changes
        useEffect(() => {
            setInputValue(page + 1)
        }, [page])

        const handleInputChange = (e) => {
            const value = e.target.value
            if (/^\d*$/.test(value)) setInputValue(value)
        }

        const handleInputBlur = () => {
            const num = Number(inputValue)
            if (num >= 1 && num <= totalPages) {
                onPageChange(null, num - 1)
            } else {
                setInputValue(page + 1)
            }
        }

        const handleKeyDown = (e) => {
            if (e.key === "Enter") handleInputBlur()
        }

        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    // gap: 1,
                }}
            >
                {/* Left button */}
                <IconButton
                    onClick={() => onPageChange(null, page - 1)}
                    disabled={page === 0}
                    sx={{
                        '&:disabled': {
                            opacity: 0.3,
                            color: 'var(--quaternary-color)',
                        }
                    }}
                >
                    <ChevronLeft />
                </IconButton>

                {/* Input field */}
                <TextField
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    size="small"
                    type="text"
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "6px",
                            width: "50px",
                            height: "36px",
                            maxWidth: "40px",
                            minWidth: "50px",
                            "& fieldset": {
                                borderColor: "var(--divider-color)",
                            },
                            "&:hover fieldset": {
                                borderColor: "var(--quaternary-color)",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: "var(--quaternary-color)",
                            },
                        },
                        "& input": {
                            color: "var(--quaternary-color)",
                            fontSize: "1rem",
                            fontFamily: "var(--secondary-content-font-family)",
                            textAlign: "center",
                        },
                    }}
                />

                {/* Total pages text */}
                <div style={{ width: "40px", marginLeft: "0.75rem" }}>
                    <Body2>of {totalPages}</Body2>
                </div>

                {/* Right button */}
                <IconButton
                    onClick={() => onPageChange(null, page + 1)}
                    disabled={page >= totalPages - 1}
                    sx={{
                        '&:disabled': {
                            opacity: 0.3,
                            color: 'var(--quaternary-color)',
                        }
                    }}
                >
                    <ChevronRight />
                </IconButton>
            </Box>
        )
    }

    return (
        <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            ActionsComponent={CustomActionControls}
            sx={{
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '1rem',
                    color: 'var(--quaternary-color)',
                    fontFamily: 'var(--secondary-content-font-family)',
                },
                '& .MuiTablePagination-select': {
                    fontSize: '1rem',
                    color: 'var(--quaternary-color)',
                    fontFamily: 'var(--secondary-content-font-family)',
                    marginRight: '0.5rem',
                },
                '& .MuiSelect-icon': {
                    color: 'var(--quaternary-color)',
                },
                '& .MuiTablePagination-toolbar': {
                    paddingLeft: '0',
                    paddingRight: '0',
                },
                '& .MuiTablePagination-displayedRows': {
                    display: 'none',
                },
            }}
        />
    )
}

/* --------------------------------------------------------------------------
🧭 HOW TO REUSE THIS PAGINATION COMPONENT

This Pagination component is **controlled**, meaning the parent must 
manage `page`, `rowsPerPage`, and their update handlers. 

It supports full control and can be reused across tables, lists, or any 
paginated UI section.

✅ Required Props:
  - `count` (number): total number of records/items.
  - `page` (number): current page index (starts from 0).
  - `rowsPerPage` (number): how many items per page.
  - `onPageChange` (function): triggered when page changes.
  - `onRowsPerPageChange` (function): triggered when rows per page changes.
  - `rowsPerPageOptions` (array, optional): dropdown options for rows per page.

---------------------------------------------------------------------------
📘 Example Usage:

import Pagination from "./Pagination"

export default function ExampleTable() {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const data = Array.from({ length: 87 }, (_, i) => ({
    id: i + 1,
    name: `Item ${i + 1}`,
  }))

  const paginatedData = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  return (
    <div>
      {paginatedData.map(item => (
        <div key={item.id}>{item.name}</div>
      ))}

      <Pagination
        count={data.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10))
          setPage(0)
        }}
      />
    </div>
  )
}

---------------------------------------------------------------------------
💡 Notes:
- The `CustomActionControls` inside provides a page number input field 
  with left/right buttons and number-only validation.
- If you need an **uncontrolled** version (self-contained state), you 
  can wrap this component and manage state internally.

--------------------------------------------------------------------------- */